<script setup>
import { computed, ref, watch } from 'vue';
import useApi from '../../Services/useApi';
import useNotification from '../../Services/useNotification';
import HarvestCreationMap from '../Map/HarvestCreationMap.vue';

const props = defineProps({
    fruits: {
        type: Array,
        required: true,
    },
    farm: {
        type: Object,
        required: true,
    },
});

const emit = defineEmits(['added']);
const api = useApi();
const notificate = useNotification();
const saving = ref(false);
const formError = ref('');
const areaSquareMeters = ref(0);
const nextTreeId = ref(1);

const harvest = ref({
    name: '',
    type: '',
    fruit_id: '',
    size: '',
    area_unit: 'ha',
    lon: '',
    lat: '',
    area: [],
    trees: [],
});

const farmArea = computed(() => (props.farm.area || []).map(point => [point.lon, point.lat]));
const mappingCategoryNames = ['frutas', 'verduras', 'hongos'];
const categorizedFruits = computed(() => props.fruits.filter(fruit => fruit.categories?.some(category =>
    mappingCategoryNames.includes(category.name.trim().toLocaleLowerCase('es'))
)));
const uncategorizedProductCount = computed(() => props.fruits.length - categorizedFruits.value.length);
const selectedFruit = computed(() => props.fruits.find(fruit => String(fruit.id) === String(harvest.value.fruit_id)));
const isPointMapping = computed(() => Boolean(selectedFruit.value?.categories?.some(category =>
    ['frutas', 'hongos'].includes(category.name.trim().toLocaleLowerCase('es'))
)));
const selectedCategoryNames = computed(() =>
    selectedFruit.value?.categories?.map(category => category.name).join(', ') || 'Sin categoría'
);

const displayArea = computed(() => {
    if (!areaSquareMeters.value) {
        return '';
    }

    return harvest.value.area_unit === 'ha'
        ? `${harvest.value.size} ha`
        : `${harvest.value.size} m²`;
});

const updateStoredArea = () => {
    if (!areaSquareMeters.value) {
        harvest.value.size = '';
        return;
    }

    harvest.value.size = harvest.value.area_unit === 'ha'
        ? Math.round((areaSquareMeters.value / 10000) * 10000) / 10000
        : Math.round(areaSquareMeters.value * 100) / 100;
};

const updateArea = payload => {
    harvest.value.area = payload.locations;
    areaSquareMeters.value = payload.area;
    harvest.value.lon = payload.center ? payload.center[0] : '';
    harvest.value.lat = payload.center ? payload.center[1] : '';
    updateStoredArea();
};

const addTreePoint = coordinates => {
    const number = nextTreeId.value++;
    harvest.value.trees.push({
        local_id: number,
        name: `Punto ${number}`,
        lon: coordinates[0],
        lat: coordinates[1],
        planted_at: new Date().toISOString().slice(0, 10),
    });
};

watch(() => harvest.value.fruit_id, () => {
    const nextType = selectedFruit.value
        ? (isPointMapping.value ? 'trees' : 'area')
        : '';

    if (harvest.value.type !== nextType) {
        harvest.value.area = [];
        harvest.value.trees = [];
        harvest.value.size = '';
        harvest.value.lon = '';
        harvest.value.lat = '';
        areaSquareMeters.value = 0;
    }

    harvest.value.type = nextType;
    formError.value = '';
});

const removeTree = index => {
    harvest.value.trees.splice(index, 1);
};

const validate = () => {
    if (!harvest.value.name.trim()) {
        return 'Escribe un nombre para esta cosecha.';
    }

    if (!harvest.value.fruit_id) {
        return 'Selecciona el producto que vas a registrar.';
    }

    if (harvest.value.type === 'area') {
        if (harvest.value.area.length < 4 || !areaSquareMeters.value) {
            return 'Dibuja el polígono del cultivo antes de guardar.';
        }
        return '';
    }

    if (!harvest.value.trees.length) {
        return 'Marca al menos un punto en el mapa.';
    }

    const incompleteTree = harvest.value.trees.find(tree =>
        !tree.name.trim() || !tree.planted_at
    );
    if (incompleteTree) {
        return 'Completa el nombre y la fecha de cada punto.';
    }

    return '';
};

const createHarvest = async () => {
    formError.value = validate();
    if (formError.value) {
        notificate.error(formError.value);
        return;
    }

    saving.value = true;
    try {
        const response = await api.addHarvest(props.farm.id, harvest.value);
        notificate.success('Cosecha guardada con éxito');
        emit('added', response.data.harvest);
    } catch (error) {
        formError.value = 'No fue posible guardar la cosecha. Revisa la información e intenta nuevamente.';
        notificate.error(formError.value);
    } finally {
        saving.value = false;
    }
};
</script>

<template>
    <form class="space-y-6" @submit.prevent="createHarvest">
        <div v-if="formError" class="p-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl" role="alert">
            {{ formError }}
        </div>

        <div class="grid gap-4 lg:grid-cols-2">
            <label>
                <span class="block mb-2 font-bold text-enriqueto_green">Nombre de la cosecha</span>
                <input
                    v-model="harvest.name"
                    type="text"
                    class="w-full p-3 bg-white border rounded-xl border-light_green"
                    placeholder="Ej. Lote de aguacates 2026"
                />
            </label>
            <label>
                <span class="block mb-2 font-bold text-enriqueto_green">Producto</span>
                <select v-model="harvest.fruit_id" class="w-full p-3 bg-white border rounded-xl border-light_green">
                    <option value="" disabled>Selecciona un producto</option>
                    <option v-for="fruit in categorizedFruits" :key="fruit.id" :value="fruit.id">{{ fruit.name }}</option>
                </select>
            </label>
        </div>

        <p v-if="uncategorizedProductCount" class="text-sm text-gray-600">
            {{ uncategorizedProductCount }} productos sin categoría están ocultos para evitar abrir el mapa equivocado.
        </p>

        <div v-if="selectedFruit" class="flex items-start gap-3 p-4 border rounded-xl border-light_green bg-light_yellow">
            <span class="material-symbols-outlined text-enriqueto_green">
                {{ isPointMapping ? 'location_on' : 'polyline' }}
            </span>
            <div>
                <p class="font-bold text-enriqueto_green">
                    {{ isPointMapping ? 'Registro automático por puntos' : 'Registro automático por polígono' }}
                </p>
                <p class="text-sm text-gray-700">
                    {{ selectedCategoryNames }}:
                    {{ isPointMapping
                        ? 'agrega un punto por cada planta. El producto se aplicará a todos automáticamente.'
                        : 'dibuja el límite del cultivo y calcularemos su área.' }}
                </p>
            </div>
        </div>

        <div v-else class="p-5 text-center text-gray-600 border-2 border-dashed rounded-xl border-light_green">
            Selecciona un producto para abrir automáticamente el mapa adecuado.
        </div>

        <template v-if="selectedFruit">
        <div>
            <h2 class="mb-1 text-xl font-bold text-enriqueto_green">
                {{ harvest.type === 'area' ? 'Área del cultivo' : 'Ubicación de los puntos' }}
            </h2>
            <p class="mb-3 text-sm text-gray-700">
                {{ harvest.type === 'area'
                    ? 'El límite amarillo corresponde a la finca. Dibuja dentro de él el área de esta cosecha.'
                    : 'El límite amarillo corresponde a la finca. Cada toque o clic agrega un punto numerado.' }}
            </p>
            <HarvestCreationMap
                :center="[Number(farm.lon), Number(farm.lat)]"
                :farm-area="farmArea"
                :mode="harvest.type"
                :tree-points="harvest.trees"
                @selected-area="updateArea"
                @tree-point-added="addTreePoint"
            />
        </div>

        <div v-if="harvest.type === 'area'" class="grid gap-4 p-4 sm:grid-cols-2 bg-light_yellow rounded-2xl">
            <label>
                <span class="block mb-2 text-sm font-bold text-enriqueto_green">Mostrar el área en</span>
                <select v-model="harvest.area_unit" class="w-full p-3 bg-white border rounded-xl border-light_green" @change="updateStoredArea">
                    <option value="ha">Hectáreas (ha)</option>
                    <option value="m2">Metros cuadrados (m²)</option>
                </select>
            </label>
            <label>
                <span class="block mb-2 text-sm font-bold text-enriqueto_green">Área calculada</span>
                <input
                    class="w-full p-3 bg-white border rounded-xl border-light_gray"
                    :value="displayArea"
                    readonly
                    placeholder="Dibuja el cultivo para calcularla"
                />
            </label>
        </div>

        <section v-else class="space-y-3">
            <div class="flex items-end justify-between gap-4">
                <div>
                    <h2 class="text-xl font-bold text-enriqueto_green">Información de cada punto</h2>
                    <p class="text-sm text-gray-700">El número de cada tarjeta coincide con el número mostrado en el mapa.</p>
                </div>
                <span class="px-3 py-1 text-sm font-bold rounded-full bg-light_yellow text-enriqueto_green">
                    {{ harvest.trees.length }} puntos
                </span>
            </div>

            <p v-if="!harvest.trees.length" class="p-4 text-center text-gray-600 border-2 border-dashed rounded-xl border-light_green">
                Toca o haz clic en el mapa para agregar el primer punto.
            </p>

            <div class="grid gap-4 lg:grid-cols-2">
                <article
                    v-for="(tree, index) in harvest.trees"
                    :key="tree.local_id"
                    class="p-4 bg-white border rounded-2xl border-light_green"
                >
                    <div class="flex items-center justify-between mb-3">
                        <h3 class="flex items-center gap-2 font-bold text-enriqueto_green">
                            <span class="flex items-center justify-center w-8 h-8 text-white rounded-full bg-enriqueto_green">{{ index + 1 }}</span>
                            Punto {{ index + 1 }}
                        </h3>
                        <button type="button" class="text-sm text-red-700 underline" @click="removeTree(index)">
                            Eliminar punto
                        </button>
                    </div>

                    <div class="grid gap-3 sm:grid-cols-3">
                        <label>
                            <span class="block mb-1 text-sm font-bold text-enriqueto_green">Nombre</span>
                            <input v-model="tree.name" type="text" class="w-full p-3 border rounded-xl border-light_green" />
                        </label>
                        <label>
                            <span class="block mb-1 text-sm font-bold text-enriqueto_green">Fecha de siembra</span>
                            <input v-model="tree.planted_at" type="date" class="w-full p-3 border rounded-xl border-light_green" />
                        </label>
                        <div>
                            <span class="block mb-1 text-sm font-bold text-enriqueto_green">Coordenadas</span>
                            <p class="p-3 text-sm bg-gray-50 rounded-xl">{{ Number(tree.lat).toFixed(6) }}, {{ Number(tree.lon).toFixed(6) }}</p>
                        </div>
                    </div>
                </article>
            </div>
        </section>
        </template>

        <div class="flex justify-end pt-2">
            <button
                type="submit"
                class="w-full px-6 py-3 font-bold text-white rounded-xl bg-enriqueto_green hover:shadow-md sm:w-auto"
                :disabled="saving"
            >
                {{ saving ? 'Guardando…' : 'Guardar cosecha' }}
            </button>
        </div>
    </form>
</template>
